import React, { useEffect, useRef, useState } from 'react';

import {
  Button,
  Grid,
  makeStyles,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Form, Formik } from 'formik';
import { get } from 'lodash';
import { TranslateFunction } from 'react-localize-redux';

import { CheckboxField } from '@fd/ui/atoms';
import { FormikInputField, FormSection } from '@fd/ui/molecules';

import { HelpdeskButton } from '../../../../../components/HelpdeskButton';
import { FormikScrollToError } from '../../../../../layouts/Portal/FormikScrollToError';
import { ImagerySection } from '../../../../ProductManagement/components/ImagerySection';
import { KeywordsListBox } from '../../components/KeywordsListBox/KeywordsListBox';
import {
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  SHORT_DESCRIPTION_MAX_LENGTH,
} from '../../constants';
import { mobileAppsService } from '../../mobileApps.services';
import { validateSubmissionForm } from '../../utils';
import { EditkeywordsModal } from '../EditKeywordsModal';

const useStyles = makeStyles((theme: Theme) => ({
  formContent: {
    paddingBottom: theme.spacing(3),
  },
  field: {
    minHeight: '5rem',
    marginTop: theme.spacing(1),
  },
  alignRight: {
    alignSelf: 'flex-end',
  },
  alignLeft: {
    alignSelf: 'flex-start',
  },
  floatRight: {
    float: 'right',
  },
  keyWordsDesciption: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  saveButtonContainer: {
    marginTop: theme.spacing(8),
    paddingRight: theme.spacing(3),
  },
}));

export type SubmissionFormProps = {
  handleSave: (values) => Promise<any>;
  translate: TranslateFunction;
  appName?: string;
  appId: string;
  mobileApp?: any;
  loading?: boolean;
};

export const SubmissionForm = (props: SubmissionFormProps): JSX.Element => {
  const { handleSave, appId, translate, loading = false, appName = '', mobileApp = {} } = props;
  const [keywords, setKeywords] = useState(
    (translate('Mobile_apps_default_keywords') as string)?.split(',')
  );
  const [editKeywordsModalOpen, setEditKeywordsModalOpen] = useState(false);
  const formRef = useRef<any>(null);
  const classes = useStyles();
  const keywordsDescription = (
    <div className={classes.keyWordsDesciption}>
      <Typography variant="caption">{translate('Keywords_explanation')}</Typography>{' '}
      <HelpdeskButton label="portalhelp:app-submission-keywordsexplanation" />
      {'.'}
    </div>
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  useEffect(() => {
    mobileApp?.Keywords && setKeywords(mobileApp?.Keywords);
  }, [mobileApp]);

  useEffect(() => {
    formRef.current?.setFieldValue('Keywords', keywords, false);
  }, [keywords]);

  return (
    <Paper>
      {editKeywordsModalOpen && (
        <EditkeywordsModal
          keywordsDescription={keywordsDescription}
          setOpen={setEditKeywordsModalOpen}
          translate={translate}
          setParentKeywords={setKeywords}
          keywords={keywords}
          open={editKeywordsModalOpen}
        />
      )}
      <Formik
        enableReinitialize={true}
        innerRef={formRef}
        initialValues={{
          AppName: mobileApp?.AppName || appName,
          AppDescription:
            mobileApp?.AppDescription ||
            translate('Submission_app_description', {
              storeName: appName,
            }),
          AppShortDescription:
            mobileApp?.AppShortDescription ||
            translate('Submission_app_short_description', { storeName: appName }),
          AutoPublish: mobileApp?.AutoPublish || true,
          AppLogoUrl: mobileApp?.AppLogoUrl || '',
          Keywords: keywords,
        }}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          await handleSave(values);
          setSubmitting(false);
        }}
        validate={(values) => {
          return validateSubmissionForm(values, translate);
        }}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form className={classes.formContent}>
            <FormikScrollToError />
            <FormSection sectionTitle={translate('Details') as string}>
              <FormikInputField
                isLoading={loading}
                FormHelperTextProps={{
                  className: get(touched, 'Name') && get(errors, 'Name') ? classes.alignRight : '',
                }}
                className={`${classes.field}`}
                fdKey={'mobile-app-submission-form-app-name'}
                name="AppName"
                label={translate('App_name') as string}
                helperText={
                  <>
                    {translate('App_name_cannot_be_changed_once_submitted')}
                    <span
                      className={classes.floatRight}
                    >{`${values.AppName.length}/${NAME_MAX_LENGTH}`}</span>
                  </>
                }
              />
              <FormikInputField
                isLoading={loading}
                className={classes.field}
                fdKey={'mobile-app-submission-form-app-desc'}
                name="AppDescription"
                multiline
                label={translate('Description') as string}
                helperText={`${values.AppDescription.length}/${DESCRIPTION_MAX_LENGTH}`}
              />
              <FormikInputField
                isLoading={loading}
                FormHelperTextProps={{
                  className: classes.alignRight,
                }}
                className={`${classes.field}`}
                fdKey={'mobile-app-submission-form-short-desc'}
                name="AppShortDescription"
                multiline
                label={translate('Short_description') as string}
                helperText={`${values.AppShortDescription.length}/${SHORT_DESCRIPTION_MAX_LENGTH}`}
              />
            </FormSection>
            <FormSection sectionTitle={translate('Keywords') as string}>
              <Grid container justify="space-between" direction="column">
                {keywordsDescription}
                <KeywordsListBox
                  height={!isMobile ? '170px' : undefined}
                  setOpen={setEditKeywordsModalOpen}
                  translate={translate}
                  keywords={keywords}
                  title={translate('Keywords') as string}
                />
              </Grid>
            </FormSection>
            <FormSection sectionTitle={translate('Logo') as string}>
              <ImagerySection
                errorMessage={(touched.AppLogoUrl && (get(errors, 'AppLogoUrl') as string)) || null}
                appId={appId}
                srcSet={values.AppLogoUrl}
                description={
                  <Typography variant="caption">{translate('Logo_upload_description')}</Typography>
                }
                imageFileName={values.AppLogoUrl}
                isLoading={false}
                onImageRemoved={() => formRef.current?.setFieldValue('AppLogoUrl', '')}
                onImageUploaded={(url: string) => {
                  formRef?.current?.setFieldTouched('AppLogoUrl', true);
                  formRef.current?.setFieldValue('AppLogoUrl', url);
                }}
                uploadImage={mobileAppsService.uploadLogo}
                translate={translate}
                smallImageTitle={translate('submission_form_small_image_title') as string}
                useFullUri={true}
              />
            </FormSection>
            <FormSection
              sectionTitle={translate('Auto_publish') as string}
              textContainerAlignSelf={'center'}
            >
              <CheckboxField
                ariaLabel={translate('Auto_publish_app_checkbox') as string}
                checked={values.AutoPublish}
                fdKey="mobile-app-submission-form-auto-publish-checkbox"
                label={translate('Automatically_publish_app_when_it_passes_review') as string}
                onChange={(isChecked) => setFieldValue('AutoPublish', isChecked)}
              />
            </FormSection>
            <Grid container className={classes.saveButtonContainer} item xs={12} justify="flex-end">
              {
                <Button
                  color="primary"
                  variant="contained"
                  data-fd="submission-save-button"
                  disabled={isSubmitting || loading}
                  type="submit"
                >
                  {translate('Save_and_continue')}
                </Button>
              }
            </Grid>
          </Form>
        )}
      </Formik>
    </Paper>
  );
};
